/**
 * Serializes an array of filters (`IFilter[]`) into a plain object
 * that can be used in API requests (e.g., query params or request body).
 *
 * This function was extracted from the `useFilters` hook to be reused
 * on both the client side (React) and the server side (SSR/middleware),
 * avoiding duplicated logic.
 *
 * @param input - Array of internal filter objects
 * @param columnsLiteralIds - Column names whose values should be treated as literal strings
 * @returns A plain object with serialized filter values
 */

import { IFilter } from '../components/TableAbstractSCN'

export const serializeFiltersPure = (
  input: IFilter[],
  columnsLiteralIds: string[] = [],
): Record<string, any> => {
  return input
    .filter(
      (ele) =>
        !!ele.value ||
        (ele.enums && ele.enums.length > 0) ||
        !!ele.from ||
        !!ele.to,
    )
    .reduce((acc, curr) => {
      switch (curr.type) {
        case 'range-slider':
          return {
            ...acc,
            [curr.column]: curr.value,
          }

        case 'date':
          return {
            ...acc,
            [curr.column]: {
              from: curr.from
                ? new Date(
                    new Date(curr.from).valueOf() +
                      new Date().getTimezoneOffset() * 60000,
                  )
                : undefined,
              to: curr.to
                ? new Date(
                    new Date(curr.to).valueOf() +
                      new Date().getTimezoneOffset() * 60000,
                  )
                : undefined,
            },
          }

        case 'async-enum':
          return {
            ...acc,
            [curr.column]: curr.enums
              ?.filter((ele) => ele?.id)
              .map((ele) => ele.id),
          }

        case 'enum':
          return {
            ...acc,
            [curr.column]: curr?.enums
              ?.map((ele, index) => {
                const optSelected = curr?.enumData?.[index]
                return {
                  ...ele,
                  id: optSelected?.id || optSelected?.value,
                  name: columnsLiteralIds.includes(curr.column)
                    ? optSelected?.value
                    : typeof optSelected?.value === 'number'
                    ? optSelected?.name
                    : optSelected?.value,
                }
              })
              .filter((ele) => ele.checked)
              .map((ele) => ({
                name: ele.name,
                id: ele.id,
              })),
          }

        case 'sort':
        case 'text':
          return {
            ...acc,
            [curr.column]: curr.value,
          }

        default:
          return acc
      }
    }, {})
}
